import { DeviceInfo } from '../types';

/**
 * 检测设备类型
 */
export const getDeviceInfo = (): DeviceInfo => {
  const userAgent = navigator.userAgent;
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);
  const isMobile = isIOS || isAndroid || screenWidth <= 768;
  const isTablet = /iPad/i.test(userAgent) || (screenWidth > 768 && screenWidth <= 1024);
  const isDesktop = !isMobile && !isTablet;
  
  // 检查是否以PWA模式运行
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                      (window.navigator as any).standalone || 
                      document.referrer.includes('android-app://');
  
  const orientation = screenWidth > screenHeight ? 'landscape' : 'portrait';
  
  return {
    isMobile,
    isTablet,
    isDesktop,
    isIOS,
    isAndroid,
    orientation,
    screenWidth,
    screenHeight,
    isStandalone
  };
};

/**
 * 获取安全区域
 */
export const getSafeArea = () => {
  const computedStyle = getComputedStyle(document.documentElement);
  
  return {
    top: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-top)') || '0'),
    right: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-right)') || '0'),
    bottom: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-bottom)') || '0'),
    left: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-left)') || '0')
  };
};

/**
 * 触摸事件处理
 */
export const preventDefaultTouch = (e: TouchEvent) => {
  if (e.touches.length > 1) {
    e.preventDefault();
  }
};

/**
 * 禁用页面缩放
 */
export const disableZoom = () => {
  document.addEventListener('touchstart', preventDefaultTouch, { passive: false });
  document.addEventListener('touchmove', preventDefaultTouch, { passive: false });
  
  // 禁用双击缩放
  let lastTouchEnd = 0;
  document.addEventListener('touchend', (event) => {
    const now = new Date().getTime();
    if (now - lastTouchEnd <= 300) {
      event.preventDefault();
    }
    lastTouchEnd = now;
  }, false);
};

/**
 * 启用页面缩放
 */
export const enableZoom = () => {
  document.removeEventListener('touchstart', preventDefaultTouch);
  document.removeEventListener('touchmove', preventDefaultTouch);
};

/**
 * 获取触摸点距离
 */
export const getTouchDistance = (touches: TouchList): number => {
  if (touches.length < 2) return 0;
  
  const touch1 = touches[0];
  const touch2 = touches[1];
  
  const dx = touch1.clientX - touch2.clientX;
  const dy = touch1.clientY - touch2.clientY;
  
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * 节流函数
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): T => {
  let timeout: NodeJS.Timeout | null = null;
  let previous = 0;
  
  return ((...args: Parameters<T>) => {
    const now = Date.now();
    const remaining = wait - (now - previous);
    
    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      return func(...args);
    } else if (!timeout) {
      timeout = setTimeout(() => {
        previous = Date.now();
        timeout = null;
        func(...args);
      }, remaining);
    }
  }) as T;
};

/**
 * 防抖函数
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): T => {
  let timeout: NodeJS.Timeout;
  
  return ((...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T;
};

/**
 * 检测设备类型
 */
export const detectDevice = () => {
  const userAgent = navigator.userAgent;
  const screenWidth = window.innerWidth;
  
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) || screenWidth <= 768;
  const isTablet = /iPad/i.test(userAgent) || (screenWidth > 768 && screenWidth <= 1024);
  
  return {
    type: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
    mobile: isMobile
  };
};

/**
 * 是否为移动设备
 */
export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
};

/**
 * 是否为iOS设备
 */
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

/**
 * 优化视口设置
 */
export const optimizeViewport = () => {
  // 确保视口元标签存在
  let viewport = document.querySelector('meta[name="viewport"]');
  if (!viewport) {
    viewport = document.createElement('meta');
    viewport.setAttribute('name', 'viewport');
    document.head.appendChild(viewport);
  }
  
  // 设置移动端优化的视口
  viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
  
  // 设置状态栏样式（iOS Safari）
  let statusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
  if (!statusBar) {
    statusBar = document.createElement('meta');
    statusBar.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
    statusBar.setAttribute('content', 'black-translucent');
    document.head.appendChild(statusBar);
  }
}; 